import {
  AuthError,
  type AuthSession,
  type AuthenticatedUser,
  type ForgotPasswordInput,
  type LoginInput,
  type RegisterInput,
  type RegistrationResult,
  type ResendVerificationCodeInput,
  type ResetPasswordInput,
  type UserRole,
  type VerifyEmailInput,
} from '@emazy/shared-types';

import type { AuthService } from './AuthService';
import type { AuthSessionStorage } from './authSessionStorage';

type MockAccount = {
  password: string;
  firstName: string;
  lastName: string;
  roles: UserRole[];
};

const mockAccounts: Record<string, MockAccount> = {
  'customer@emazy.local': {
    password: 'Customer123!',
    firstName: 'Casey',
    lastName: 'Customer',
    roles: ['customer'],
  },
  'admin@emazy.local': {
    password: 'Admin123!',
    firstName: 'Avery',
    lastName: 'Admin',
    roles: ['admin'],
  },
  'workshop@emazy.local': {
    password: 'Workshop123!',
    firstName: 'Wren',
    lastName: 'Workshop',
    roles: ['workshop'],
  },
};

export const mockDevelopmentAccounts = [
  { label: 'Customer', email: 'customer@emazy.local', password: 'Customer123!' },
  { label: 'Admin', email: 'admin@emazy.local', password: 'Admin123!' },
  { label: 'Workshop', email: 'workshop@emazy.local', password: 'Workshop123!' },
] as const;

const normalizeEmail = (email: string) => email.trim().toLowerCase();

function createUser(email: string, account: MockAccount): AuthenticatedUser {
  return {
    id: `mock-${account.roles[0]}-001`,
    email,
    emailVerified: true,
    firstName: account.firstName,
    lastName: account.lastName,
    displayName: `${account.firstName} ${account.lastName}`,
    roles: account.roles,
    createdAt: '2026-01-01T00:00:00.000Z',
  };
}

export class MockAuthService implements AuthService {
  constructor(
    private readonly persistentStorage: AuthSessionStorage,
    private readonly temporaryStorage: AuthSessionStorage,
    private readonly delayMs = import.meta.env.MODE === 'test' ? 0 : 160,
  ) {}

  private async wait() {
    if (this.delayMs > 0) await new Promise((resolve) => window.setTimeout(resolve, this.delayMs));
  }

  async getCurrentSession() {
    await this.wait();
    return this.temporaryStorage.read() ?? this.persistentStorage.read();
  }

  async login(input: LoginInput): Promise<AuthSession> {
    await this.wait();
    const email = normalizeEmail(input.email);

    if (email === 'unverified@emazy.local') {
      throw new AuthError({
        code: 'email-not-verified',
        message: 'Verify your email before signing in.',
        field: 'email',
      });
    }
    if (email === 'locked@emazy.local') {
      throw new AuthError({
        code: 'too-many-attempts',
        message: 'Too many attempts. Please wait and try again.',
      });
    }
    if (email === 'reset@emazy.local') {
      throw new AuthError({
        code: 'password-reset-required',
        message: 'Reset your password before signing in.',
        field: 'password',
      });
    }

    const account = mockAccounts[email];
    if (!account || account.password !== input.password) {
      throw new AuthError({
        code: 'invalid-credentials',
        message: 'Email or password is incorrect.',
      });
    }

    const session: AuthSession = {
      user: createUser(email, account),
      expiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
    };
    this.persistentStorage.clear();
    this.temporaryStorage.clear();
    (input.rememberMe ? this.persistentStorage : this.temporaryStorage).write(session);
    return session;
  }

  async register(input: RegisterInput): Promise<RegistrationResult> {
    await this.wait();
    const email = normalizeEmail(input.email);
    if (email === 'existing@emazy.local' || mockAccounts[email]) {
      throw new AuthError({
        code: 'email-already-exists',
        message: 'An account already uses this email.',
        field: 'email',
      });
    }
    return { email, verificationRequired: true };
  }

  async verifyEmail(input: VerifyEmailInput) {
    await this.wait();
    if (input.code === '000000') {
      throw new AuthError({
        code: 'expired-verification-code',
        message: 'This verification code has expired.',
        field: 'code',
      });
    }
    if (input.code !== '123456') {
      throw new AuthError({
        code: 'invalid-verification-code',
        message: 'Enter the valid six-digit code.',
        field: 'code',
      });
    }
  }

  async resendVerificationCode(input: ResendVerificationCodeInput) {
    void input;
    await this.wait();
  }

  async forgotPassword(input: ForgotPasswordInput) {
    void input;
    await this.wait();
  }

  async resetPassword(input: ResetPasswordInput) {
    await this.wait();
    if (input.code !== '123456') {
      throw new AuthError({
        code: 'invalid-verification-code',
        message: 'Enter the valid six-digit code.',
        field: 'code',
      });
    }
  }

  async logout() {
    await this.wait();
    this.persistentStorage.clear();
    this.temporaryStorage.clear();
  }

  async getAccessToken() {
    return (await this.getCurrentSession()) ? 'mock-development-access-token' : null;
  }
}
